import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api, inr } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

// Filter attributes shown per mart (dynamic by category)
const MART_FILTERS = {
  "home-mart": ["brand", "price"],
  "style-mart": ["brand", "price"],
  "wood-mart": ["type", "brand", "price"],
  "tech-mart": ["brand", "price"],
  default: ["brand", "price"],
};

const TYPE_LABELS = {
  standard: "Standard", branded_furniture: "Branded", vendor: "Vendor Collection", signature: "Signature",
};

export default function ProductListing({ mart, martConfig, extraParams = {}, title }) {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const category = params.get("category") || "";
  const subcategory = params.get("subcategory") || "";
  const brand = params.get("brand") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "";
  const ptype = params.get("type") || "";
  const maxPrice = params.get("max_price") || "";

  const filtersFor = MART_FILTERS[mart] || MART_FILTERS.default;

  useEffect(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (mart) qp.set("mart", mart);
    if (category) qp.set("category", category);
    if (subcategory) qp.set("subcategory", subcategory);
    if (brand) qp.set("brand", brand);
    if (search) qp.set("search", search);
    if (sort) qp.set("sort", sort);
    if (maxPrice) qp.set("max_price", maxPrice);
    if (ptype) qp.set("product_type", ptype === "branded" ? "branded_furniture" : ptype);
    Object.entries(extraParams).forEach(([k, v]) => qp.set(k, v));
    qp.set("limit", "60");
    api.get(`/products?${qp.toString()}`).then((r) => { setItems(r.data.items); setTotal(r.data.total); }).finally(() => setLoading(false));
  }, [mart, category, subcategory, brand, search, sort, ptype, maxPrice, JSON.stringify(extraParams)]);

  const brands = useMemo(() => [...new Set(items.map((i) => i.brand))].sort(), [items]);
  const categories = martConfig ? Object.keys(martConfig.categories || {}) : [];
  const subcats = martConfig && category ? (martConfig.categories[category] || []) : [];

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k === "category") next.delete("subcategory");
    setParams(next);
  };
  const clearAll = () => setParams(search ? new URLSearchParams({ search }) : new URLSearchParams());

  const Filters = () => (
    <div className="space-y-6">
      {categories.length > 0 && (
        <div>
          <h4 className="mb-3 text-sm font-bold text-midnight">Category</h4>
          <div className="space-y-1.5">
            <button onClick={() => setParam("category", "")} className={`block text-sm ${!category ? "font-bold text-gold-dark" : "text-muted-foreground hover:text-midnight"}`}>All</button>
            {categories.map((c) => (
              <button key={c} data-testid={`filter-category-${c}`} onClick={() => setParam("category", c)} className={`block text-sm ${category === c ? "font-bold text-gold-dark" : "text-muted-foreground hover:text-midnight"}`}>{c}</button>
            ))}
          </div>
        </div>
      )}
      {subcats.length > 0 && (
        <div>
          <h4 className="mb-3 text-sm font-bold text-midnight">Type</h4>
          <div className="space-y-1.5">
            {subcats.map((c) => (
              <button key={c} onClick={() => setParam("subcategory", subcategory === c ? "" : c)} className={`block text-sm ${subcategory === c ? "font-bold text-gold-dark" : "text-muted-foreground hover:text-midnight"}`}>{c}</button>
            ))}
          </div>
        </div>
      )}
      {filtersFor.includes("type") && (
        <div>
          <h4 className="mb-3 text-sm font-bold text-midnight">Collection</h4>
          <div className="space-y-1.5">
            {["", "branded_furniture", "vendor", "signature"].map((t) => (
              <button key={t || "all"} onClick={() => setParam("type", t === "branded_furniture" ? "branded" : t)}
                className={`block text-sm ${(ptype === (t === "branded_furniture" ? "branded" : t)) ? "font-bold text-gold-dark" : "text-muted-foreground hover:text-midnight"}`}>
                {t ? TYPE_LABELS[t] : "All"}
              </button>
            ))}
          </div>
        </div>
      )}
      {filtersFor.includes("brand") && brands.length > 1 && (
        <div>
          <h4 className="mb-3 text-sm font-bold text-midnight">Brand</h4>
          <div className="space-y-1.5 max-h-52 overflow-auto hide-scrollbar">
            {brands.map((b) => (
              <label key={b} className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-midnight">
                <input type="checkbox" checked={brand === b} onChange={() => setParam("brand", brand === b ? "" : b)} className="accent-gold" />{b}
              </label>
            ))}
          </div>
        </div>
      )}
      {filtersFor.includes("price") && (
        <div>
          <h4 className="mb-3 text-sm font-bold text-midnight">Max Price</h4>
          <input type="range" min="500" max="70000" step="500" value={maxPrice || 70000} onChange={(e) => setParam("max_price", e.target.value)} className="w-full accent-gold" />
          <p className="text-sm font-semibold text-midnight">{maxPrice ? `Up to ${inr(maxPrice)}` : "Any price"}</p>
        </div>
      )}
      <button onClick={clearAll} className="text-sm font-semibold text-red-600 hover:underline" data-testid="clear-filters">Clear all filters</button>
    </div>
  );

  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-midnight">{title || (search ? `Results for "${search}"` : "Shop All")}</h1>
          <p className="text-sm text-muted-foreground">{total} products</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowFilters(true)} className="lg:hidden flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold" data-testid="mobile-filters-btn"><SlidersHorizontal className="h-4 w-4" /> Filters</button>
          <select data-testid="sort-select" value={sort} onChange={(e) => setParam("sort", e.target.value)} className="rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold outline-none">
            <option value="">Sort: Relevance</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      <div className="flex gap-8">
        <aside className="hidden lg:block w-60 shrink-0"><Filters /></aside>
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse rounded-2xl bg-secondary" />)}</div>
          ) : items.length === 0 ? (
            <div className="grid place-items-center rounded-3xl border border-dashed border-border py-24 text-center">
              <p className="text-lg font-bold text-midnight">No products found</p>
              <p className="text-sm text-muted-foreground">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="product-grid">
              {items.map((p, i) => <Reveal key={p.id} delay={(i % 8) * 0.03}><ProductCard product={p} /></Reveal>)}
            </div>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-auto rounded-t-3xl bg-white p-6">
            <div className="mb-4 flex items-center justify-between"><h3 className="text-lg font-bold">Filters</h3><button onClick={() => setShowFilters(false)}><X className="h-6 w-6" /></button></div>
            <Filters />
            <button onClick={() => setShowFilters(false)} className="mt-6 w-full rounded-full bg-midnight py-3 font-bold text-white">Show {total} products</button>
          </div>
        </div>
      )}
    </div>
  );
}
